import { ReactNode, useEffect, useRef, useState } from "react";
import { FirebaseApp } from "firebase/app"
import { getAuth, signInAnonymously, User } from "firebase/auth";
import { getFirestore, collection, doc, setDoc, onSnapshot } from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";

import {
  TRANSFORM_FOLDER_NAME,
  FORMAT_100x135,
  FORMAT_75x100,
  MAX_NUM_PHOTOS,
  STATE_INPUT,
  STATE_UPLOADING,
  STATE_TRANSFORMING,
  STATE_DONE,
  STATE_ERROR,
} from "./constants";
import logo from "./images/logo.svg";
import cardsImage from "./images/cards.svg";
import pinwheel from "./images/pinwheel.gif";
import spinner from "./images/Hourglass.gif";
import { splitToChunks } from "./utils/arrays";
import { DimensionsOfImageWithBorder, FileInfo, UploadDocument } from "./types";

type AppProps = {
  firebaseApp: FirebaseApp
}

const App: React.FC<AppProps> = ({ firebaseApp }) => {
  const [user, setUser] = useState<User>();
  const [state, setState] = useState(STATE_INPUT);
  const [filesSelectedCount, setFilesSelectedCount] = useState(0);
  const [idUpload, setIdUpload] = useState(generateIdUpload());
  const [format, setFormat] = useState(FORMAT_75x100);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [transformedCount, setTransformedCount] = useState(0);
  const [archive, setArchive] = useState<FileInfo>();

  const auth = getAuth(firebaseApp);
  const db = getFirestore(firebaseApp);
  const storage = getStorage(firebaseApp);

  console.log('userId: ', user?.uid)

  useEffect(() => {
    signInAnonymously(auth)
      .then((creds) => {
        setUser(creds.user)
      })
      .catch((error) => {
        console.error(error)
      });
  }, [auth]);

  useEffect(() => {
    if (filesSelectedCount > 0 && secondStepSectionRef.current) {
      secondStepSectionRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [filesSelectedCount]);

  useEffect(() => {
    if (!user) return;
    if (!idUpload) return;

    return onSnapshot(doc(db, `users/${user.uid}/uploads/${idUpload}`), (snap) => {
      if (!snap.exists()) return;
      const uploadDoc = snap.data() as UploadDocument;

      setTransformedCount(uploadDoc.transformed);

      if (uploadDoc.transformationStatus === "ERROR") {
        setState(STATE_ERROR);

        setDoc(doc(collection(db, `users/${user.uid}/errors`)), {
          user: user.uid,
          idUpload,
          appearedAt: new Date(),
        })
      }

      if (!uploadDoc.archive) {
        return;
      }
      setArchive(uploadDoc.archive);
      setState(STATE_DONE);
    })
  }, [user, idUpload, db]);

  const fileInpRef = useRef<HTMLInputElement>(null);
  const secondStepSectionRef = useRef<HTMLElement>(null);

  async function uploadToStorage(fileList: FileList): Promise<FileInfo[]> {
    const filesArr = [];
    for (let i = 0; i < fileList.length; i++) {
      filesArr.push(fileList[i]);
    }

    const filesChunksArr = splitToChunks(filesArr, 4);
    const snapsArr = [];

    for (let i = 0; i < filesChunksArr.length; i++) {
      const snaps = await Promise.all(
        filesChunksArr[i].map(async (file) => {
          const storageRef = ref(storage, `${TRANSFORM_FOLDER_NAME}/${user!.uid}/${idUpload}/${file.name}`);
          const res = await uploadBytes(storageRef, file, { contentType: file.type });

          setUploadedCount((prev) => prev + 1);
          return res;
        }
        )
      );

      snapsArr.push(...snaps);
    }

    return snapsArr.map((snap) => ({
      name: snap.ref.name,
      storagePath: snap.ref.fullPath,
    }));
  }

  function handleClickOneMoreTime() {
    setFilesSelectedCount(0);
    setIdUpload(generateIdUpload());
    setState(STATE_INPUT);
    setUploadedCount(0);
    setTransformedCount(0);
    setArchive(undefined);
    fileInpRef.current!.value = "";
  }

  async function handleClickFormat(format: DimensionsOfImageWithBorder) {
    setFormat(format);
    setState(STATE_UPLOADING);
    const filesStorageInfoArr = await uploadToStorage(
      fileInpRef.current!.files!
    );

    setState(STATE_TRANSFORMING);

    await setDoc(doc(db, `users/${user!.uid}/uploads`, idUpload), {
      uploadedDate: new Date(),
      files: filesStorageInfoArr,
      targetFormat: formatToString(format),
      transformed: 0,
    })
  }

  const screenInput = (
    <section className="with-centered-vetical-content">
      <h2 className="screen-reader">Выбор файлов и формата</h2>
      <img src={cardsImage} alt="Decorative. Photos of cute bears" />
      <p className="text-description">
        Загрузите свои фото, а мы аккуратно адаптируем их под формат, который
        идеально подходит для Важной&nbsp;книги 7,5×10 или&nbsp;10×13,5
        <br />
        <br />
        Также, мы нарисуем белые рамочки 5&nbsp;мм и повернем фото, чтобы было
        удобно отправить их на печать.
      </p>
      <section className="with-centered-vetical-content">
        <h3>1 шаг</h3>
        <p className="h2 instruction">Выберите до 50 фотографий</p>
        <Button black onClick={() => fileInpRef.current!.click()}>
          Выбрать
        </Button>
        <p>
          {filesSelectedCount <= MAX_NUM_PHOTOS ? (
            <small>Выбрано {filesSelectedCount} фото</small>
          ) : (
            <b className="error font-weight-normal">
              Выбрано слишком много фото ({filesSelectedCount} шт.),
              ограничьтесь пятьюдесятью
            </b>
          )}
        </p>
      </section>

      {filesSelectedCount > 0 && filesSelectedCount <= MAX_NUM_PHOTOS && (
        <section
          className="with-centered-vetical-content"
          ref={secondStepSectionRef}
        >
          <h3>2 шаг</h3>
          <p className="h2 instruction">Выберите формат фотографий</p>
          <Button onClick={() => handleClickFormat(FORMAT_75x100)}>
            {formatToString(FORMAT_75x100)}
          </Button>
          <Button
            className="mt10"
            onClick={() => handleClickFormat(FORMAT_100x135)}
          >
            {formatToString(FORMAT_100x135)}
          </Button>
        </section>
      )}
    </section>
  );

  const screenDone = state === STATE_DONE && archive && (
    <>
      <p className="callout">
        Готово!
        <br />
        Скачайте и разархивируйте:
        <br />
        <a
          id="link-download-archive"
          href={archive.publicUrl}
          className="link-download"
        >
          {archive.name}
        </a>
      </p>
      <p id="print-instructions-link">
        <a href="https://vazhnayakniga.ru/guide#rec293418716">
          Инструкция куда и как отправить фото на печать
        </a>
      </p>
      <Button black onClick={() => handleClickOneMoreTime()}>
        Хочу ещё разок
      </Button>
    </>
  );

  const screenUploading = state === STATE_UPLOADING && (
    <>
      <img src={pinwheel} width="64" alt="upload process spinner" />
      <p>
        Загружаем... {uploadedCount}&nbsp;/&nbsp;
        {fileInpRef.current!.files!.length}
      </p>
    </>
  );

  const screenTransforming = state === STATE_TRANSFORMING && (
    <>
      <img src={spinner} width="64" alt="transformation process spinner" />
      <p>
        {transformedCount < fileInpRef.current!.files!.length ? (
          <>
            Трансформируем... {transformedCount}&nbsp;/&nbsp;
            {fileInpRef.current!.files!.length}
          </>
        ) : (
          <>Еще чуть-чуть...</>
        )}
      </p>
    </>
  );

  const screenError = state === STATE_ERROR && (
    <>
      <h2 className="mt0">Что-то пошло не так :(</h2>
      <p className="text-description">
        Скоро придет программист и разберется с ошибкой. Но попробуйте,
        пожалуйста, еще разок.
      </p>
      <Button black onClick={() => handleClickOneMoreTime()}>
        Попытаться снова
      </Button>
    </>
  );

  return (
    <div className="vazh-layout with-centered-vetical-content">
      <header className="vazh-layout__header">
        <img src={logo} alt="Logo of Vazhnaya Kniga" />
        <h1 className="screen-reader">
          Адаптирование фотографий под формат подходящий для Важной книги
        </h1>
      </header>
      <main className="vazh-layout__content with-centered-vetical-content">
        <input
          type="file"
          ref={fileInpRef}
          multiple
          hidden
          accept=".jpg, .jpeg, .heic, .png"
          onChange={(e) => {
            setFilesSelectedCount(e.target.files!.length);
          }}
        />
        {state === STATE_INPUT && screenInput}
        {[STATE_UPLOADING, STATE_TRANSFORMING, STATE_DONE].includes(state) && (
          <section className="with-centered-vetical-content">
            <h2 className="mt0">Формат {formatToString(format)}</h2>
            {screenUploading}
            {screenTransforming}
            {screenDone}
          </section>
        )}
        {state === STATE_ERROR && screenError}
      </main>
      <footer>
        <p className="no-margins">
          Запрограммировал
          <br />
          <a href="mailto:upmaten@gmail.com">Евгений Матвиенко</a>
        </p>
      </footer>
    </div>
  );
}

function generateIdUpload() {
  return uuidv4().slice(0, 20);
}

function formatToString(format: DimensionsOfImageWithBorder) {
  return `${format.width / 10}x${format.height / 10}`.replace(".", ",");
}

type ButtonProps = {
  children: ReactNode;
  black?: boolean;
  className?: string;
  [other: string]: any;
};

function Button({
  children,
  black = false,
  className = "",
  ...props
}: ButtonProps) {
  const classesStr = `${black ? "button_black" : "button_orange"
    } ${className}`.trim();

  return (
    <button className={classesStr} {...props}>
      {children}
    </button>
  );
}

export default App;
